import React, { useState } from 'react'
import { PieChart as RechartsPieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts'
import { Square } from '../../../utility/square/Square'

interface PieChartData {
  name: string
  value: number
  color: string
}

interface AiPieChartProps {
  title: string
  subtitle: string
  data?: PieChartData[]
}

export const AiPieChart: React.FC<AiPieChartProps> = ({ title, subtitle, data = [] }) => {
  const totalValue = data.reduce((sum, item) => sum + item.value, 0)
  const [activeIndex, setActiveIndex] = useState<number | null>(null)

  const handleMouseEnter = (data: any, index: number) => {
    setActiveIndex(index)
  }

  const handleMouseLeave = () => {
    setActiveIndex(null)
  }

  return (
    <Square className='w-full max-w-[336px] py-[12px] px-[24px]'>
      <div className={'flex flex-col'}>
        <div className={'text-[20px] text-focus-500'}>{title}</div>
        <div className={'text-[14px] opacity-50'}>{subtitle}</div>
      </div>
      <div className='flex w-full flex-col items-center'>
        <ResponsiveContainer height={150} width={150}>
          <RechartsPieChart>
            <Pie
              cx='50%'
              cy='50%'
              data={data}
              dataKey='value'
              innerRadius={30}
              outerRadius={60}
              paddingAngle={2}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              {data.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={entry.color}
                  opacity={activeIndex === null || activeIndex === index ? 1 : 0.6}
                />
              ))}
            </Pie>
            <text
              dominantBaseline='middle'
              fill={'#00326D'}
              style={{ fontSize: '14px' }}
              textAnchor='middle'
              x='50%'
              y='50%'
            >
              {totalValue}
            </text>
            <text
              dominantBaseline='middle'
              fill={'#1A2B4D99'}
              style={{ fontSize: '10px' }}
              textAnchor='middle'
              x='50%'
              y='60%'
            >
              100%
            </text>

            <Tooltip
              content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  return (
                    <div className='min-h-[24px] rounded-[12px] bg-focus-500 py-[8px] px-[12px] text-[14px] font-medium leading-[20px]'>
                      <p className='font-medium text-white'>{payload[0].name}</p>
                      <p className='text-aqua-300'>{payload[0].value}</p>
                    </div>
                  )
                }
                return null
              }}
            />
          </RechartsPieChart>
        </ResponsiveContainer>

        <div className='mt-4 flex w-full flex-col gap-2'>
          {data.map((entry, index) => {
            const percentage = Math.round((entry.value / totalValue) * 100)
            return (
              <div
                key={index}
                style={{
                  opacity: activeIndex === null || activeIndex === index ? 1 : 0.6,
                }}
                className='flex cursor-pointer items-center justify-between gap-2 transition-opacity'
                onMouseEnter={() => handleMouseEnter(entry, index)}
                onMouseLeave={handleMouseLeave}
              >
                <div className={'flex items-center gap-[8px]'}>
                  <div
                    className='h-[10px] w-[10px] rounded-full'
                    style={{ backgroundColor: entry.color }}
                  />
                  <span className='text-sm text-gray-700'>{entry.name}</span>
                </div>
                <span className='text-sm text-gray-500'>{percentage}%</span>
              </div>
            )
          })}
        </div>
      </div>
    </Square>
  )
}
